import {
  ChangeDetectorRef,
  Directive,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ColumnDefinition } from '../../model/column-definition';
import ListViewColumn, { ListColumn } from '../../model/list-view-column';
import { ListRow, ListValue, ListViewRow } from '../../model/list-view-row';
import { MenuItemDefinition } from '../../model/menu-item-definition';
import { ModelBase } from '../../model/model-base';
import { ListViewColumnFactory } from '../../service/factory/list-view-column-factory.service';
import { ListViewHeaderComponent } from './list-view-header/list-view-header.component';
import { State, StateChangeEvent } from './list-view-header/list-view-header.interface';
import { LanguageService } from '../../service/language.service';

export type RowClickEvent<TSource = any> = {
  context: TSource;
  event: PointerEvent;
};

@Directive({
    standalone: false
})
export abstract class ListViewBase<TSource = any, TValue = any, TValueOriginal = TValue> implements OnDestroy, DoCheck {
  constructor(
    private readonly _columnFactory: ListViewColumnFactory,
    languageService: LanguageService,
    iterableDiffers: IterableDiffers
  ) {
    this._s = languageService.onLangChange.subscribe(e => {
      this._columns.forEach(col => (col.locale = e.lang));
      this.resetRows();
    });
    this.itemsSourceDiffer = iterableDiffers.find([]).create(null);
  }

  private readonly itemsSourceDiffer: IterableDiffer<TSource>;
  cdr = inject(ChangeDetectorRef);

  ngDoCheck(): void {
    this.resetRowsWhenItemsSourceDiffer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.resetRowsWhenItemsSourceDiffer();
  }

  @Input() contextMenuDefinition: MenuItemDefinition[];
  @Input() isContextMenuDefinitionInline: boolean;
  @Output() rowClick = new EventEmitter<RowClickEvent<TSource>>();
  @Output() onColumnChange = new EventEmitter<ListViewColumn<TSource>[]>();
  @ViewChildren(ListViewHeaderComponent) headers: QueryList<ListViewHeaderComponent>;

  @Input() itemsSource: TSource[];
  @Input() preferencesKey: string;
  @Input() rowFactory?: (source: TSource) => ListViewRow<TSource, TValue, TValueOriginal>;
  @Input() rowTrackBy?: (row: ListViewRow<TSource, TValue, TValueOriginal>) => unknown;

  private _columnDefinition: ColumnDefinition<TSource, TValue>[];
  get columnDefinition(): ColumnDefinition<TSource, TValue>[] {
    return this._columnDefinition;
  }
  @Input() set columnDefinition(value: ColumnDefinition<TSource, TValue>[]) {
    this._columnDefinition = value;
    this.resetColumns();
  }

  protected recentId: string = undefined; // used to highlight recently viewed item
  private _columns: ListViewColumn<TSource, TValue, TValueOriginal>[];
  private _rows: ListViewRow<TSource, TValue, TValueOriginal>[] = [];
  private _s: Subscription;

  // intended for html binding:
  columns: ListViewColumn<TSource, TValue, TValueOriginal>[] = [];
  rows: ListViewRow<TSource, TValue, TValueOriginal>[] = [];

  private updateColumns(): void {
    this.columns = this._columns;
    // (could add event logic here if needed)
    this.onColumnChange.emit(this._columns);
    this.cdr.detectChanges();
  }

  private updateRows(): void {
    if (this.recentId) {
      this._rows.forEach(x => (x.highlight = (x.dataSource as ModelBase)?.id === this.recentId));
    }
    this.rows = this._rows;
    // (could add event logic here if needed)
  }

  protected resetColumns(): void {
    if (this.columnDefinition) {
      const existingColumns = this._columns;
      const previousState = new Map<string, { selected: boolean; index: number }>();
      existingColumns?.forEach((column, index) => previousState.set(column.binding, { selected: column.selected, index }));

      this._columns = this._columnFactory.create(this.columnDefinition);

      this._columns.forEach(column => {
        const state = previousState.get(column.binding);
        if (state) {
          column.selected = state.selected;
        }
      });

      this._columns.sort((left, right) => {
        const leftIndex = previousState.get(left.binding)?.index ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = previousState.get(right.binding)?.index ?? Number.MAX_SAFE_INTEGER;
        return leftIndex - rightIndex;
      });
    }
    this.updateColumns();
  }

  protected resetRows(): void {
    this._rows = [];
    if (this.itemsSource) {
      this._rows = this.itemsSource.map(x => (x instanceof ListViewRow ? x : this.rowFactory?.(x) ?? new ListViewRow(x)));
      this.headers?.forEach(h => h.updateState());

      if (this.itemsSource.length > 0) {
        const modelItem = this.itemsSource[0] as ModelBase;
        this.recentId = modelItem?.consumeRecentId?.() || undefined;
      }
    }
    this.updateRows();
  }

  protected sortRowsByColumn(state: State, column: ListColumn<TSource, TValue, TValueOriginal>): void {
    switch (state) {
      case State.DEFAULT:
        this.resetRows();
        return;
      case State.ORDERBYASC:
        this._rows.sort((a, b) => a.compareByColumn(b, column));
        this.updateRows();
        return;
      case State.ORDERBYDESC:
        this._rows.sort((a, b) => b.compareByColumn(a, column));
        this.updateRows();
        return;
    }
  }

  get totalCount(): number {
    return this.rows?.length || 0;
  }
  get totalCountPlaceholder(): string {
    return this.totalCount === 1 ? 'COMMON.ITEM' : 'COMMON.ITEMS';
  }
  get selectedColumns(): ListViewColumn<TSource, TValue, TValueOriginal>[] {
    return this._columns?.filter(x => x.selected) || [];
  }
  get hasContextMenu(): boolean {
    return this.contextMenuDefinition?.length > 0 && !this.isContextMenuDefinitionInline;
  }

  get hasInlineContextMenu(): boolean {
    return this.isContextMenuDefinitionInline;
  }

  get isEmpty(): boolean {
    return this.itemsSource && this._rows.length === 0;
  }

  ColumnChanged(columns?: ListViewColumn<TSource, TValue, TValueOriginal>[]) {
    if (columns) {
      this._columns = columns;
    }
    this.onColumnChange.emit(this._columns);
  }

  ngOnDestroy() {
    this._s?.unsubscribe();
    this._s = null;
  }

  onRowClicked(row: ListRow<TSource, TValue, TValueOriginal>, event: PointerEvent) {
    (row.dataSource as ModelBase)?.storeRecentId?.(); // so we can highlight it when the user navigates back
    this.rowClick.emit({ context: row.dataSource, event: event });
  }

  trackRow(row: ListViewRow<TSource, TValue, TValueOriginal>): unknown {
    return this.rowTrackBy ? this.rowTrackBy(row) : row;
  }

  onStateChange(e: StateChangeEvent) {
    const columnId = e.payload.id;
    const column = this.selectedColumns.find(x => x.id === columnId);

    if (!column.canSort) return;

    if (this.headers) {
      this.headers.filter(x => x.id !== columnId).forEach(x => x.toDefault());
    }

    const sortOrder = e.payload.state;
    this.sortRowsByColumn(sortOrder, column);
  }

  sumValues(column: ListViewColumn<TSource, TValue, TValueOriginal>): { displayValue: TValue }[] {
    const summaries: ListValue<TValue, TValueOriginal>[] = [];

    this.rows.forEach(row => row.addToSummaryValue(summaries, column));
    summaries.forEach(sum => (sum.displayValue = column.converter ? column.converter(sum.value) : (sum.value as unknown as TValue)));

    return summaries;
  }

  private resetRowsWhenItemsSourceDiffer(): void {
    if (this.itemsSourceDiffer.diff(this.itemsSource)) {
      this.resetRows();
    }
  }
}
